import { useState, useEffect, useCallback } from 'react';
import { useAdminRegistration } from '../../authentication_components/adminRegistrationContext';
import { 
  CheckCircle, 
  XCircle, 
  Clock, 
  User, 
  Mail, 
  Phone, 
  Calendar,
  Shield,
  AlertTriangle,
  RefreshCw,
  UserCheck,
  Search,
  Filter,
  ArrowLeft,
  MoreHorizontal,
  Save,
  Edit,
  Lock,
  Trash2, // New icon for delete
} from 'lucide-react';


// --- Admin Password Reset Dialog (remains the same) ---
const AdminPasswordResetDialog = ({ admin, onReset, onClose, loading, error, validatePassword }) => {
  const [newPassword, setNewPassword] = useState('');
  const [passwordErrors, setPasswordErrors] = useState([]);

  useEffect(() => {
      setPasswordErrors(validatePassword(newPassword));
  }, [newPassword, validatePassword]);
  
  const isFormValid = newPassword.length > 0 && passwordErrors.length === 0;

  const handleSubmit = (e) => {
    e.preventDefault();
    if (isFormValid) {
        onReset(admin.admin_id, newPassword);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-2xl shadow-2xl max-w-sm w-full">
        <div className="p-6">
          <div className="flex items-center justify-between border-b pb-4 mb-4">
            <h3 className="text-xl font-semibold text-gray-900 flex items-center">
              <Lock className="h-5 w-5 mr-2 text-red-600" />
              Restablecer Contraseña
            </h3>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <XCircle className="h-6 w-6" />
            </button>
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-800 text-sm">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <p className="text-gray-600">
                Restablecer la contraseña para **{admin.admin_first_name} {admin.admin_last_name}**.
            </p>

            {/* New Password Field */}
            <div>
              <label htmlFor="new_password" className="block text-sm font-medium text-gray-700">Nueva Contraseña</label>
              <input 
                type="password"
                id="new_password"
                name="new_password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2 focus:ring-red-500 focus:border-red-500"
              />
            </div>
            
            {/* Password Validation Errors */}
            {newPassword.length > 0 && passwordErrors.length > 0 && (
                <div className="text-red-600 text-xs space-y-1">
                    {passwordErrors.map((err, index) => (
                        <p key={index} className="flex items-center">
                            <XCircle className="h-3 w-3 mr-1 flex-shrink-0" /> {err}
                        </p>
                    ))}
                </div>
            )}
            
            <div className="flex space-x-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                disabled={loading}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50 transition-colors"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={loading || !isFormValid}
                className="flex-1 px-4 py-2 rounded-lg text-white font-medium bg-red-600 hover:bg-red-700 disabled:opacity-50 transition-colors"
              >
                {loading ? (
                  <div className="flex items-center justify-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2"></div>
                    Restableciendo...
                  </div>
                ) : (
                  <span className='flex items-center justify-center'><Lock className="h-4 w-4 mr-2" /> Restablecer</span>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};
// --- End Admin Password Reset Dialog ---


// --- New Admin Delete Confirmation Dialog ---
const AdminDeleteConfirmDialog = ({ admin, onDelete, onClose, loading, error }) => {
    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full">
                <div className="p-6">
                    <div className="flex items-center justify-center mb-6">
                        <div className={`h-16 w-16 rounded-full flex items-center justify-center bg-red-100`}>
                            <Trash2 className="h-8 w-8 text-red-600" />
                        </div>
                    </div>

                    <div className="text-center mb-6">
                        <h3 className="text-xl font-semibold text-gray-900 mb-2">
                            Confirmar Eliminación
                        </h3>
                        <p className="text-gray-600 mb-4">
                            **ADVERTENCIA:** ¿Está seguro de que desea eliminar la cuenta de:
                        </p>
                        
                        <div className="bg-gray-50 rounded-lg p-4">
                            <div className="text-left space-y-2">
                                <div><strong>Nombre:</strong> {admin.admin_first_name} {admin.admin_last_name}</div>
                                <div><strong>Email:</strong> {admin.admin_email}</div>
                                <div><strong>ID:</strong> {admin.admin_id}</div>
                            </div>
                        </div>
                        <p className="text-red-600 text-sm mt-3 font-medium">
                            Esta acción es **permanente** y eliminará todos los datos asociados al administrador (se espera un borrado en cascada).
                        </p>
                    </div>

                    {error && (
                        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-800 text-sm">
                            {error}
                        </div>
                    )}
                    
                    <div className="flex space-x-3">
                        <button
                            onClick={onClose}
                            disabled={loading}
                            className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50 transition-colors"
                        >
                            Cancelar
                        </button>
                        <button
                            onClick={() => onDelete(admin.admin_id)}
                            disabled={loading}
                            className={`flex-1 px-4 py-2 rounded-lg text-white font-medium bg-red-600 hover:bg-red-700 disabled:opacity-50 transition-colors`}
                        >
                            {loading ? (
                                <div className="flex items-center justify-center">
                                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2"></div>
                                    Eliminando...
                                </div>
                            ) : (
                                <span className='flex items-center justify-center'><Trash2 className="h-4 w-4 mr-2" /> Confirmar Eliminación</span>
                            )}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};
// --- End Admin Delete Confirmation Dialog ---


// --- Updated Admin Edit Dialog ---
const AdminEditDialog = ({ admin, formData, onSave, onClose, onChange, onPasswordReset, onDeleteConfirm, loading, error }) => {
  const isFormValid = formData.admin_first_name?.trim() && formData.admin_last_name?.trim() && formData.admin_email?.trim();
  const allStatuses = ['active', 'inactive', 'suspended', 'pending'];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex items-center justify-between border-b pb-4 mb-4">
            <h3 className="text-xl font-semibold text-gray-900 flex items-center">
              <Edit className="h-5 w-5 mr-2 text-orange-600" />
              Editar Administrador
            </h3>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <XCircle className="h-6 w-6" />
            </button>
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-800 text-sm">
              {error}
            </div>
          )}

          <form onSubmit={onSave} className="space-y-4">
            {/* ... Form fields (Admin ID, Name, Email, Phone, Status) remain the same ... */}
            <div>
              <label className="block text-sm font-medium text-gray-700">ID de Administrador</label>
              <input type="text" value={admin.admin_id} readOnly className="mt-1 block w-full border border-gray-200 rounded-md shadow-sm p-2 bg-gray-50 cursor-not-allowed text-sm" />
            </div>
            <div>
              <label htmlFor="admin_first_name" className="block text-sm font-medium text-gray-700">Primer Nombre <span className="text-red-500">*</span></label>
              <input type="text" id="admin_first_name" name="admin_first_name" value={formData.admin_first_name || ''} onChange={onChange} required className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2 focus:ring-orange-500 focus:border-orange-500" />
            </div>
            <div>
              <label htmlFor="admin_last_name" className="block text-sm font-medium text-gray-700">Apellido <span className="text-red-500">*</span></label>
              <input type="text" id="admin_last_name" name="admin_last_name" value={formData.admin_last_name || ''} onChange={onChange} required className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2 focus:ring-orange-500 focus:border-orange-500" />
            </div>
            <div>
              <label htmlFor="admin_email" className="block text-sm font-medium text-gray-700">Email <span className="text-red-500">*</span></label>
              <input type="email" id="admin_email" name="admin_email" value={formData.admin_email || ''} onChange={onChange} required className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2 focus:ring-orange-500 focus:border-orange-500" />
            </div>
            <div>
              <label htmlFor="admin_phone" className="block text-sm font-medium text-gray-700">Teléfono</label>
              <input type="text" id="admin_phone" name="admin_phone" value={formData.admin_phone || ''} onChange={onChange} className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2 focus:ring-orange-500 focus:border-orange-500" />
            </div>
            <div>
              <label htmlFor="access_status" className="block text-sm font-medium text-gray-700">Estado de Acceso <span className="text-red-500">*</span></label>
              <select id="access_status" name="access_status" value={formData.access_status || ''} onChange={onChange} required className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2 focus:ring-orange-500 focus:border-orange-500">
                {allStatuses.map(status => (<option key={status} value={status}>{status.charAt(0).toUpperCase() + status.slice(1)}</option>))}
              </select>
            </div>
            
            <div className='flex justify-between space-x-3 pt-6 border-t mt-6'>
                <button
                    type="button"
                    onClick={onDeleteConfirm} // NEW BUTTON
                    className="flex-1 inline-flex items-center justify-center px-4 py-2 border border-red-300 text-sm font-medium rounded-lg text-red-700 bg-red-50 hover:bg-red-100 transition-colors"
                >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Eliminar Admin
                </button>
                <button
                    type="button"
                    onClick={onPasswordReset}
                    className="flex-1 inline-flex items-center justify-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-lg text-gray-700 bg-gray-50 hover:bg-gray-100 transition-colors"
                >
                    <Lock className="h-4 w-4 mr-2" />
                    Restablecer Contraseña
                </button>
            </div>

            <div className="flex space-x-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                disabled={loading}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50 transition-colors"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={loading || !isFormValid}
                className="flex-1 px-4 py-2 rounded-lg text-white font-medium bg-orange-600 hover:bg-orange-700 disabled:opacity-50 transition-colors"
              >
                {loading ? (
                  <div className="flex items-center justify-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2"></div>
                    Guardando...
                  </div>
                ) : (
                  <span className='flex items-center justify-center'><Save className="h-4 w-4 mr-2" /> Guardar Cambios</span>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};
// --- End Updated Admin Edit Dialog ---


export default function AdminApproval() {
  const [pendingAdmins, setPendingAdmins] = useState([]);
  const [allAdmins, setAllAdmins] = useState([]);
  const [filteredAdmins, setFilteredAdmins] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('pending');
  const [selectedAdmin, setSelectedAdmin] = useState(null);
  
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showResetPasswordDialog, setShowResetPasswordDialog] = useState(false);
  const [showDeleteConfirmDialog, setShowDeleteConfirmDialog] = useState(false); // NEW STATE
  const [editFormData, setEditFormData] = useState({});

  const [actionLoading, setActionLoading] = useState(false);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    pageSize: 20,
    totalPages: 0,
    totalRecords: 0
  });
  
  const [successMessage, setSuccessMessage] = useState(null);

  const {
    listAdmins,
    updateAdmin,
    resetAdminPassword,
    deleteAdmin, // NEW FUNCTION
    loading,
    error,
    clearError,
    validatePassword,
    isMasterUser
  } = useAdminRegistration();

  // Load admins on component mount (omitted for brevity)
  useEffect(() => {
    if (isMasterUser) {
      loadAdmins();
    }
  }, [isMasterUser, pagination.currentPage, statusFilter]);

  // Filter admins based on search term (omitted for brevity)
  useEffect(() => {
    if (!searchTerm) {
      setFilteredAdmins(allAdmins);
    } else {
      const filtered = allAdmins.filter(admin => 
        admin.admin_first_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        admin.admin_last_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        admin.admin_email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        admin.admin_id.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredAdmins(filtered);
    }
  }, [searchTerm, allAdmins]);

  const loadAdmins = async () => {
    clearError();
    setSuccessMessage(null);
    try {
      const options = {
        page: pagination.currentPage,
        pageSize: pagination.pageSize,
        accessStatus: statusFilter === 'all' ? '' : statusFilter,
        sortBy: 'created_at',
        sortOrder: 'desc'
      };

      const result = await listAdmins(options);
      
      if (result.success) {
        setAllAdmins(result.admins || []);
        setPagination(result.pagination || {});
        
        const pending = result.admins?.filter(admin => admin.admin_access_status === 'pending') || [];
        setPendingAdmins(pending);
      }
    } catch (err) {
      console.error('Failed to load admins:', err);
    }
  };
  
  // --- EDIT DIALOG LOGIC (omitted repeated functions) ---
  const handleOpenEditDialog = (admin) => {
    setSelectedAdmin(admin);
    setEditFormData({
        admin_first_name: admin.admin_first_name,
        admin_last_name: admin.admin_last_name,
        admin_email: admin.admin_email,
        admin_phone: admin.admin_phone || '', 
        access_status: admin.admin_access_status,
    });
    setShowEditDialog(true);
  };

  const handleCloseEditDialog = () => {
    setShowEditDialog(false);
    setSelectedAdmin(null);
    setEditFormData({});
    clearError();
  };
  
  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setEditFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSaveEdit = async (e) => {
    e.preventDefault();
    if (!selectedAdmin) return;
    
    setActionLoading(true);
    clearError();

    try {
        const updatePayload = Object.fromEntries(
            Object.entries(editFormData).filter(([_, v]) => v !== '' && v !== null)
        );

        const result = await updateAdmin(selectedAdmin.admin_id, updatePayload);
        
        await loadAdmins();
        setSuccessMessage(`Administrador ${result.admin.admin_first_name} ${result.admin.admin_last_name} actualizado exitosamente.`);
        handleCloseEditDialog();
    } catch (err) {
        // Error state is handled by the context/component
    } finally {
        setActionLoading(false);
    }
  };
  
  // --- PASSWORD RESET LOGIC (omitted repeated functions) ---
  const handleOpenResetPasswordDialog = () => {
    setShowEditDialog(false);
    setShowResetPasswordDialog(true);
    clearError();
  };

  const handleCloseResetPasswordDialog = () => {
    setShowResetPasswordDialog(false);
    clearError();
  };

  const handleResetPassword = async (adminId, newPassword) => {
    setActionLoading(true);
    clearError();

    try {
        const result = await resetAdminPassword(adminId, newPassword);
        
        setSuccessMessage(`Contraseña para Admin ID: ${result.admin_id} restablecida exitosamente.`);
        handleCloseResetPasswordDialog();
        setSelectedAdmin(null);
    } catch (err) {
        // Error state is handled by the context/component
    } finally {
        setActionLoading(false);
    }
  };

  // --- NEW DELETE LOGIC ---
  const handleOpenDeleteConfirmDialog = () => {
    setShowEditDialog(false);
    setShowDeleteConfirmDialog(true);
    clearError();
  };

  const handleCloseDeleteConfirmDialog = () => {
    setShowDeleteConfirmDialog(false);
    clearError();
  };

  const handleDeleteAdmin = async (adminId) => {
    setActionLoading(true);
    clearError();

    try {
        const result = await deleteAdmin(adminId);
        
        await loadAdmins();
        setSuccessMessage(`Administrador '${result.admin_id}' eliminado exitosamente.`);
        handleCloseDeleteConfirmDialog();
        setSelectedAdmin(null);
    } catch (err) {
        // Error state is handled by the context/component
    } finally {
        setActionLoading(false);
    }
  };
  // --- END NEW DELETE LOGIC ---


  const getStatusBadge = (status) => {
    // ... (remains the same)
    const statusConfig = {
      pending: { color: 'bg-yellow-100 text-yellow-800 border-yellow-200', icon: Clock },
      active: { color: 'bg-green-100 text-green-800 border-green-200', icon: CheckCircle },
      inactive: { color: 'bg-red-100 text-red-800 border-red-200', icon: XCircle },
      suspended: { color: 'bg-gray-100 text-gray-800 border-gray-200', icon: Shield }
    };

    const config = statusConfig[status] || statusConfig.pending;
    const IconComponent = config.icon;

    return (
      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${config.color}`}>
        <IconComponent className="h-3 w-3 mr-1" />
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  const formatDate = (dateString) => {
    // ... (remains the same)
    try {
      return new Date(dateString).toLocaleDateString('es-ES', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateString;
    }
  };

  if (!isMasterUser) {
    // ... (Access Restricted Component remains the same)
    return (
      <div className="bg-gradient-to-br from-red-50 to-orange-50 min-h-screen flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-2xl p-8 border border-red-200">
          <div className="text-center">
            <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-red-100 mb-6">
              <AlertTriangle className="h-8 w-8 text-red-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              Acceso Restringido
            </h2>
            <p className="text-gray-600 mb-6">
              Solo usuarios maestros pueden gestionar administradores.
            </p>
            <button
              onClick={() => window.history.back()}
              className="w-full bg-gray-100 hover:bg-gray-200 text-gray-800 font-medium py-2 px-4 rounded-lg transition-colors"
            >
              Volver Atrás
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gradient-to-br from-orange-50 to-orange-100 min-h-screen">
      {/* ... Header (remains the same) ... */}
      <div className="bg-white border-b border-orange-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <button
                onClick={() => window.history.back()}
                className="mr-4 p-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <ArrowLeft className="h-5 w-5" />
              </button>
              <div>
                <h2 className="text-2xl font-bold text-gray-800 flex items-center">
                  <UserCheck className="h-6 w-6 text-orange-500 mr-3" />
                  Gestión de Administradores
                </h2>
                <p className="text-gray-600 mt-2">
                  Revise, apruebe o modifique las cuentas de administrador.
                </p>
              </div>
            </div>
            
            <div className="flex items-center space-x-4">
              {/* Pending Count Badge */}
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg px-4 py-2">
                <div className="flex items-center">
                  <Clock className="h-5 w-5 text-yellow-600 mr-2" />
                  <div>
                    <div className="text-sm font-medium text-yellow-800">
                      {pendingAdmins.length} Pendientes
                    </div>
                    <div className="text-xs text-yellow-600">
                      Requieren aprobación
                    </div>
                  </div>
                </div>
              </div>

              <button
                onClick={loadAdmins}
                disabled={loading}
                className="flex items-center px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 disabled:opacity-50 transition-colors"
              >
                <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                Actualizar
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Success Display */}
        {successMessage && (
            <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
                <div className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                    <div className="flex-1">
                        <p className="text-green-800 font-medium">Éxito</p>
                        <p className="text-green-700 text-sm mt-1">{successMessage}</p>
                    </div>
                    <button
                        onClick={() => setSuccessMessage(null)}
                        className="text-green-400 hover:text-green-600 ml-2"
                    >
                        ×
                    </button>
                </div>
            </div>
        )}

        {/* Error Display */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex items-start">
              <AlertTriangle className="h-5 w-5 text-red-600 mr-2 mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <p className="text-red-800 font-medium">Error</p>
                <p className="text-red-700 text-sm mt-1">{error}</p>
              </div>
              <button
                onClick={clearError}
                className="text-red-400 hover:text-red-600 ml-2"
              >
                ×
              </button>
            </div>
          </div>
        )}

        {/* Filters and Search (remains the same) */}
        <div className="bg-white rounded-lg shadow-sm border border-orange-200 p-6 mb-6">
          <div className="flex flex-col sm:flex-row gap-4">
            {/* Search */}
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Buscar por nombre, email o ID..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                />
              </div>
            </div>

            {/* Status Filter */}
            <div className="sm:w-48">
              <div className="relative">
                <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="w-full pl-10 pr-8 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 appearance-none bg-white"
                >
                  <option value="pending">Solo Pendientes</option>
                  <option value="active">Solo Activos</option>
                  <option value="inactive">Solo Inactivos</option>
                  <option value="suspended">Solo Suspendidos</option>
                  <option value="all">Todos los Estados</option>
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* Loading State (remains the same) */}
        {loading && (
          <div className="bg-white rounded-lg shadow-sm border border-orange-200 p-8">
            <div className="flex items-center justify-center">
              <div className="animate-spin rounded-full h-8 w-8 border-4 border-orange-500 border-t-transparent mr-3"></div>
              <span className="text-gray-600">Cargando administradores...</span>
            </div>
          </div>
        )}

        {/* Admins List (updated for table/card clicks) */}
        {!loading && (
          <div className="bg-white rounded-lg shadow-sm border border-orange-200 overflow-hidden">
            {filteredAdmins.length === 0 ? (
              <div className="p-8 text-center">
                <UserCheck className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  {searchTerm ? 'No se encontraron administradores' : 'No hay administradores'}
                </h3>
                <p className="text-gray-500">
                  {searchTerm 
                    ? 'Intente con otros términos de búsqueda'
                    : `No hay administradores con estado "${statusFilter}"`
                  }
                </p>
              </div>
            ) : (
              <>
                {/* Desktop Table (only relevant changes shown) */}
                <div className="hidden lg:block overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    {/* ... Thead remains the same ... */}
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Administrador</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Contacto</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Estado</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Fecha de Registro</th>
                        <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Acciones</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {filteredAdmins.map((admin, index) => (
                        <tr 
                          key={admin.admin_id} 
                          className={`cursor-pointer ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'} hover:bg-orange-50 transition-colors`}
                          onClick={() => handleOpenEditDialog(admin)}
                        >
                          {/* ... other TDs remain the same ... */}
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <div className="h-10 w-10 rounded-full bg-orange-100 flex items-center justify-center">
                                <User className="h-5 w-5 text-orange-600" />
                              </div>
                              <div className="ml-4">
                                <div className="text-sm font-medium text-gray-900">
                                  {admin.admin_first_name} {admin.admin_last_name}
                                </div>
                                <div className="text-sm text-gray-500">
                                  ID: {admin.admin_id}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="space-y-1">
                              <div className="flex items-center text-sm text-gray-900">
                                <Mail className="h-4 w-4 text-gray-400 mr-2" />
                                {admin.admin_email}
                              </div>
                              {admin.admin_phone && (
                                <div className="flex items-center text-sm text-gray-500">
                                  <Phone className="h-4 w-4 text-gray-400 mr-2" />
                                  {admin.admin_phone}
                                </div>
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {getStatusBadge(admin.admin_access_status)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            <div className="flex items-center">
                              <Calendar className="h-4 w-4 text-gray-400 mr-2" />
                              {formatDate(admin.created_at)}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-center text-sm font-medium">
                            <button
                                onClick={(e) => {
                                    e.stopPropagation(); 
                                    handleOpenEditDialog(admin);
                                }}
                                className="inline-flex items-center px-3 py-1 border border-transparent text-xs font-medium rounded-md text-white bg-orange-600 hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500"
                                title="Editar/Modificar Administrador"
                            >
                                <MoreHorizontal className="h-3 w-3 mr-1" />
                                Editar
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Mobile Cards (only relevant changes shown) */}
                <div className="lg:hidden">
                  {filteredAdmins.map((admin) => (
                    <div 
                      key={admin.admin_id} 
                      className="border-b border-gray-200 p-4 cursor-pointer hover:bg-orange-50 transition-colors"
                      onClick={() => handleOpenEditDialog(admin)}
                    >
                      <div className="space-y-3">
                        {/* ... Info and Contact sections remain the same ... */}
                        <div className="flex items-center justify-between">
                          <div className="flex items-center">
                            <div className="h-10 w-10 rounded-full bg-orange-100 flex items-center justify-center">
                              <User className="h-5 w-5 text-orange-600" />
                            </div>
                            <div className="ml-3">
                              <div className="text-sm font-medium text-gray-900">
                                {admin.admin_first_name} {admin.admin_last_name}
                              </div>
                              <div className="text-xs text-gray-500">ID: {admin.admin_id}</div>
                            </div>
                          </div>
                          <div className='flex items-center space-x-2'>
                            {getStatusBadge(admin.admin_access_status)}
                            <button
                                onClick={(e) => {
                                    e.stopPropagation(); 
                                    handleOpenEditDialog(admin);
                                }}
                                className="p-1 border border-transparent text-xs font-medium rounded-md text-white bg-orange-600 hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500"
                            >
                                <Edit className="h-4 w-4" />
                            </button>
                          </div>
                        </div>
                        {/* Contact */}
                        <div className="space-y-1">
                          <div className="flex items-center text-sm text-gray-600">
                            <Mail className="h-4 w-4 mr-2" />
                            {admin.admin_email}
                          </div>
                          {admin.admin_phone && (
                            <div className="flex items-center text-sm text-gray-600">
                              <Phone className="h-4 w-4 mr-2" />
                              {admin.admin_phone}
                            </div>
                          )}
                        </div>

                        {/* Date */}
                        <div className="flex items-center text-sm text-gray-600">
                          <Calendar className="h-4 w-4 mr-2" />
                          Registrado: {formatDate(admin.created_at)}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        )}
      </main>

      {/* Admin Edit Dialog */}
      {showEditDialog && selectedAdmin && (
        <AdminEditDialog 
          admin={selectedAdmin}
          formData={editFormData}
          onSave={handleSaveEdit}
          onClose={handleCloseEditDialog}
          onChange={handleFormChange}
          onPasswordReset={handleOpenResetPasswordDialog}
          onDeleteConfirm={handleOpenDeleteConfirmDialog} // NEW PROP
          loading={actionLoading}
          error={error}
        />
      )}
      
      {/* Admin Password Reset Dialog */}
      {showResetPasswordDialog && selectedAdmin && (
        <AdminPasswordResetDialog 
          admin={selectedAdmin}
          onReset={handleResetPassword}
          onClose={handleCloseResetPasswordDialog}
          loading={actionLoading}
          error={error}
          validatePassword={validatePassword}
        />
      )}

      {/* Admin Delete Confirm Dialog */}
      {showDeleteConfirmDialog && selectedAdmin && (
        <AdminDeleteConfirmDialog
          admin={selectedAdmin}
          onDelete={handleDeleteAdmin}
          onClose={handleCloseDeleteConfirmDialog}
          loading={actionLoading}
          error={error}
        />
      )}
    </div>
  );
}